import React, { createContext, useContext, useMemo } from 'react';
import {
  usePermissions,
  PermissionData,
  PermissionChecks
} from '../hooks/usePermissions';

interface RBACContextValue extends PermissionChecks {
  permissionData: PermissionData | null;
}

const RBACContext = createContext<RBACContextValue | undefined>(undefined);

interface RBACProviderProps {
  children: React.ReactNode;
  permissionData: PermissionData | null;
}

/**
 * RBAC Provider component that provides permission context to all slideout components
 * @param children - Child components
 * @param permissionData - Permission data from API response
 */
export const RBACProvider: React.FC<RBACProviderProps> = ({
  children,
  permissionData
}) => {
  const permissions = usePermissions(permissionData);

  const contextValue = useMemo(
    () => ({
      ...permissions,
      permissionData
    }),
    [permissions, permissionData]
  );

  return (
    <RBACContext.Provider value={contextValue}>{children}</RBACContext.Provider>
  );
};

/**
 * Hook to access RBAC context
 * @returns RBAC context with all permission checks
 * Returns default read-only permissions if used outside RBACProvider
 */
export const useRBAC = (): RBACContextValue => {
  const context = useContext(RBACContext);

  // Return default read-only permissions if used outside provider
  // This prevents errors during component initialization or when slideout is closed
  if (context === undefined) {
    return {
      canEdit: false,
      canRead: true,
      canView: true,
      isAdmin: false,
      hasViewerRole: false,
      hasEditorRole: false,
      canAddItems: false,
      canEditItems: false,
      canDeleteItems: false,
      canManageContacts: false,
      canEditOverview: false,
      canEditDecisions: false,
      canEditAdditionalInfo: false,
      canEditMilestones: false,
      canEditGeneralInfo: false,
      permissionData: null
    };
  }

  return context;
};

/**
 * Higher-order component to conditionally render content based on permissions
 * @param children - Content to render if permission check passes
 * @param permissionCheck - Function that takes permissions and returns boolean
 * @param fallback - Optional fallback content to render if permission check fails
 */
interface ConditionalRenderProps {
  children: React.ReactNode;
  permissionCheck: (permissions: PermissionChecks) => boolean;
  fallback?: React.ReactNode;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  permissionCheck,
  fallback = null
}) => {
  const permissions = useRBAC();

  return permissionCheck(permissions) ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component to wrap edit-only controls
 */
export const EditOnlyWrapper: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback = null }) => (
  <ConditionalRender
    permissionCheck={permissions => permissions.canEdit}
    fallback={fallback}
  >
    {children}
  </ConditionalRender>
);

/**
 * Component to wrap admin-only controls
 */
export const AdminOnlyWrapper: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback = null }) => (
  <ConditionalRender
    permissionCheck={permissions => permissions.isAdmin}
    fallback={fallback}
  >
    {children}
  </ConditionalRender>
);

/**
 * Component to wrap view-only content (shown when user can read but not edit)
 */
export const ViewOnlyWrapper: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <ConditionalRender
    permissionCheck={permissions => permissions.canView && !permissions.canEdit}
  >
    {children}
  </ConditionalRender>
);
